// frontend/src/pages/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import Sidebar from '../components/Sidebar';
import { ProjectList } from '../components/ProjectList';
import ProjectForm from '../components/ProjectForm';
import ProjectDetails from '../components/ProjectDetails';
import api from '../services/api';
import Settings from './Settings';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb',
      lighter: 'rgba(37, 99, 235, 0.08)',
      dark: '#1e40af',
    },
    secondary: {
      main: '#7c3aed',
      lighter: 'rgba(124, 58, 237, 0.08)',
    },
    background: {
      default: 'rgb(250, 250, 250)',
      paper: '#ffffff',
    },
    grey: {
      50: 'rgba(0,0,0,0.02)',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
    },
    text: {
      primary: '#1e293b',
      secondary: '#475569',
    },
    divider: '#e2e8f0',
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
          borderRadius: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
  },
});

const drawerWidth = 240;
const collapsedWidth = 65;

function Dashboard({ onLogout }) {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const response = await api.get('/user');
        setUserEmail(response.data.email);
      } catch (error) {
        console.error('Failed to fetch user email:', error);
      }
    };
    fetchUserEmail();
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <Sidebar 
          open={drawerOpen} 
          onDrawerToggle={handleDrawerToggle} 
          userEmail={userEmail}
          onLogout={onLogout}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            backgroundColor: 'background.default',
            minHeight: '100vh',
            width: `calc(100% - ${drawerOpen ? drawerWidth : collapsedWidth}px)`,
            ml: 0,
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="projects" replace />} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="projects/create" element={<ProjectForm />} />
            <Route path="projects/:id/*" element={<ProjectDetails />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default Dashboard;